'use client'

import React, {
  createContext,
  memo,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'

export interface PopupItem {
  id: string
  priority: number
  component: React.ReactNode
  delay?: number // Delay in milliseconds (optional)
}

interface PopupQueueContextType {
  queue: PopupItem[]
  enqueuePopup: (popup: PopupItem) => void
  dequeuePopup: (id: string) => void
  getCurrentPopup: () => PopupItem | null
  closeCurrentPopup: () => void
}

const PopupQueueContext = createContext<PopupQueueContextType | undefined>(
  undefined
)

export const usePopupQueue = () => {
  const context = useContext(PopupQueueContext)
  if (context === undefined) {
    throw new Error('usePopupQueue must be used within a PopupQueueProvider')
  }
  return context
}

interface PopupQueueProviderProps {
  children: ReactNode
  defaultDelay?: number // Default delay in milliseconds (3-5 seconds)
}

// Memoized component for rendering popup
const PopupRenderer = memo(({ popup }: { popup: PopupItem | null }) => {
  return popup?.component || null
})

PopupRenderer.displayName = 'PopupRenderer'

export const PopupQueueProvider: React.FC<PopupQueueProviderProps> = ({
  children,
  defaultDelay = 1000, // Default 4 seconds
}) => {
  const [queue, setQueue] = useState<PopupItem[]>([])
  const [currentPopup, setCurrentPopup] = useState<PopupItem | null>(null)
  const [isReady, setIsReady] = useState(false)

  // Initialize delay timer
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsReady(true)
    }, defaultDelay)

    return () => clearTimeout(timer)
  }, [defaultDelay])

  const sortQueue = useCallback((items: PopupItem[]) => {
    return [...items].sort((a, b) => b.priority - a.priority)
  }, [])

  const updateCurrentPopup = useCallback(
    (newQueue: PopupItem[]) => {
      if (newQueue.length === 0) {
        setCurrentPopup(null)
      } else if (
        !currentPopup ||
        !newQueue.find((item) => item.id === currentPopup.id)
      ) {
        // Only show popup if ready and no delay is specified, or if delay has passed
        const nextPopup = newQueue[0]
        if (isReady && (!nextPopup.delay || nextPopup.delay <= 0)) {
          setCurrentPopup(nextPopup)
        }
      }
    },
    [currentPopup, isReady]
  )

  const enqueuePopup = useCallback(
    (popup: PopupItem) => {
      setQueue((prevQueue) => {
        const exists = prevQueue.find((item) => item.id === popup.id)
        const newQueue = exists
          ? prevQueue.map((item) => (item.id === popup.id ? popup : item))
          : [...prevQueue, popup]
        const sortedQueue = sortQueue(newQueue)

        // If popup has custom delay, set up timer
        if (popup.delay && popup.delay > 0) {
          setTimeout(() => {
            updateCurrentPopup(sortedQueue)
          }, popup.delay)
        } else {
          updateCurrentPopup(sortedQueue)
        }

        return sortedQueue
      })
    },
    [sortQueue, updateCurrentPopup]
  )

  const dequeuePopup = useCallback(
    (id: string) => {
      setQueue((prevQueue) => {
        const newQueue = prevQueue.filter((item) => item.id !== id)
        updateCurrentPopup(newQueue)
        return newQueue
      })
    },
    [updateCurrentPopup]
  )

  const getCurrentPopup = useCallback(() => currentPopup, [currentPopup])

  const closeCurrentPopup = useCallback(() => {
    if (currentPopup) {
      dequeuePopup(currentPopup.id)
    }
  }, [currentPopup, dequeuePopup])

  const contextValue = {
    queue,
    enqueuePopup,
    dequeuePopup,
    getCurrentPopup,
    closeCurrentPopup,
  }

  return (
    <PopupQueueContext.Provider value={contextValue}>
      {children}
      <PopupRenderer popup={currentPopup} />
    </PopupQueueContext.Provider>
  )
}
